import React, { useState, useContext, useEffect, useMemo } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaPlus,
  FaTrash,
  FaEdit,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import ProjectPreOrderLayoutWrapper from "../../components/ProjectPreOrderLayoutWrapper";
import { useNavigate } from "react-router-dom";

const BASE_URL = "https://nissi-india.com";

// ✅ safe localStorage getter
const getLS = (key) => {
  try {
    return localStorage.getItem(key) || "";
  } catch {
    return "";
  }
};

const WorkLocationList = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();

  /* ✅ localStorage values */
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchId = getLS("branchId") || getLS("branchid");
  const branchType = getLS("branchType") || getLS("branchtype");
  const userId = getLS("userId") || getLS("loginuserid");

  /* ✅ common headers */
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId],
  );

  /* ===================== STATE ===================== */
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [selectedIds, setSelectedIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  /* ===================== API ===================== */
  const fetchWorkLocations = async (overrideSearch) => {
    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/api/workLocation/list`, {
        params: {
          page,
          pageSize,
          search: overrideSearch !== undefined ? overrideSearch : searchTerm,
        },
        headers: commonHeaders,
      });

      if (!res.data.success) {
        toast.error("Failed to load work locations");
        return;
      }

      setRecords(res.data.list || []);
      setTotal(res.data.pagination?.total || 0);
      setTotalPages(res.data.pagination?.totalPages || 1);
      setSelectedIds([]);
    } catch (err) {
      console.error(err);
      toast.error("Server error while loading data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, commonHeaders]);

  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };

  const handleSearchSubmit = () => {
    if (page === 1) {
      fetchWorkLocations();
    } else {
      setPage(1);
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearchSubmit();
    }
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    if (page === 1) {
      fetchWorkLocations("");
    } else {
      setPage(1);
    }
  };

  /* ===================== HANDLERS ===================== */
  const handleSelect = (id, checked) => {
    if (checked) {
      setSelectedIds((prev) => [...prev, id]);
    } else {
      setSelectedIds((prev) => prev.filter((x) => x !== id));
    }
  };

  const handleRemoveSelected = async () => {
    if (selectedIds.length === 0) {
      toast.error("Please select records to remove");
      return;
    }

    const ok = window.confirm(
      `Are you sure you want to delete ${selectedIds.length} record(s)?`,
    );
    if (!ok) return;

    try {
      const res = await axios.post(
        `${BASE_URL}/api/workLocation/delete`,
        { ids: selectedIds },
        { headers: commonHeaders },
      );

      if (!res.data.success) {
        toast.error(res.data.message || "Delete failed");
        return;
      }

      if (res.data.usedIds?.length > 0) {
        toast.warn(
          `Some records not deleted because used in Projects: ${res.data.usedIds.join(
            ", ",
          )}`,
        );
      }

      toast.success(res.data.message || "Deleted successfully");
      fetchWorkLocations();
    } catch (err) {
      console.error("Delete Error:", err);
      toast.error(
        err?.response?.data?.message || "Server error while deleting",
      );
    }
  };

  const goToAdd = () => {
    navigate("/OrderManagement/WorkLocationForm");
  };

  const goToEdit = (row) => {
    navigate(`/OrderManagement/WorkLocationForm/${row.wlid}`);
  };

  /* ===================== RENDER ===================== */
  return (
    <ProjectPreOrderLayoutWrapper>
      <div
        className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}
      >
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">WORK LOCATION MASTER</span>
          }
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-4">
              <h1
                className={`text-2xl font-bold ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                Work Location List
              </h1>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark
                    ? "bg-blue-900 text-blue-100"
                    : "bg-blue-100 text-blue-800"
                }`}
              >
                {total} Records
              </span>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  placeholder="Search name, code, address..."
                  className={`px-3 py-2 rounded-lg border text-sm w-64 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                  }`}
                />
                <button
                  onClick={handleSearchSubmit}
                  className={`px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${buttonClass} ${hoverClass} text-white text-sm`}
                >
                  Search
                </button>
                {searchTerm && (
                  <button
                    onClick={handleClearSearch}
                    className={`px-4 py-2 rounded-lg font-semibold border transition-all duration-200 text-sm ${
                      isDark
                        ? "bg-gray-700 hover:bg-gray-600 text-white border-gray-600"
                        : "bg-gray-100 hover:bg-gray-200 text-gray-700 border-gray-300"
                    }`}
                  >
                    Clear
                  </button>
                )}
              </div>

              <button
                onClick={goToAdd}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transition ${buttonClass} ${hoverClass} text-white`}
              >
                <FaPlus />
                Add New
              </button>
            </div>
          </div>

          <div
            className={`rounded-xl shadow-sm border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${buttonClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">
                      Slno
                    </th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">
                      Name
                    </th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">
                      Code
                    </th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">
                      Address1
                    </th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">
                      Address2
                    </th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">
                      Edit
                    </th>
                    <th className="px-4 py-4 text-center font-semibold">
                      Select
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-6 text-center">
                        Loading...
                      </td>
                    </tr>
                  ) : records.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-6 text-center">
                        No records found
                      </td>
                    </tr>
                  ) : (
                    records.map((record, index) => {
                      const slno = (page - 1) * pageSize + index + 1;
                      const checked = selectedIds.includes(record.wlid);

                      return (
                        <tr
                          key={record.wlid}
                          className={`transition-colors duration-200 ${
                            index % 2 === 0
                              ? isDark
                                ? "bg-gray-800"
                                : "bg-gray-50"
                              : isDark
                                ? "bg-gray-900"
                                : "bg-white"
                          }`}
                        >
                          <td
                            className={`px-4 py-3 whitespace-nowrap text-sm text-center border-r border-gray-200 dark:border-gray-700 ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {slno}
                          </td>

                          <td
                            className={`px-4 py-3 whitespace-nowrap text-sm text-center border-r border-gray-200 dark:border-gray-700 font-medium ${
                              isDark ? "text-blue-300" : "text-blue-600"
                            }`}
                          >
                            {record.wlname}
                          </td>

                          <td
                            className={`px-4 py-3 whitespace-nowrap text-sm text-center border-r border-gray-200 dark:border-gray-700 ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {record.wlcode}
                          </td>

                          <td
                            className={`px-4 py-3 whitespace-nowrap text-sm text-center border-r border-gray-200 dark:border-gray-700 ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {record.addr1 || "-"}
                          </td>

                          <td
                            className={`px-4 py-3 whitespace-nowrap text-sm text-center border-r border-gray-200 dark:border-gray-700 ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {record.addr2 || "-"}
                          </td>

                          <td className="px-4 py-3 text-center border-r border-gray-200 dark:border-gray-700">
                            <button
                              onClick={() => goToEdit(record)}
                              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg font-semibold shadow hover:shadow-md transition-all ${
                                isDark
                                  ? "bg-gray-700 hover:bg-gray-600 text-white"
                                  : "bg-gray-200 hover:bg-gray-300 text-gray-800"
                              }`}
                            >
                              <FaEdit />
                              Edit
                            </button>
                          </td>

                          <td className="px-4 py-3 text-center">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) =>
                                handleSelect(record.wlid, e.target.checked)
                              }
                              className={`rounded focus:ring-2 focus:ring-blue-500 ${
                                isDark
                                  ? "bg-gray-700 border-gray-600 text-blue-600"
                                  : "bg-white border-gray-300 text-blue-600"
                              }`}
                            />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex justify-between items-center mt-4">
            <div className="flex items-center gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="px-3 py-2 bg-gray-300 rounded disabled:opacity-50"
              >
                <FaChevronLeft />
              </button>

              <span className="font-semibold">
                Page {page} / {totalPages}
              </span>

              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-2 bg-gray-300 rounded disabled:opacity-50"
              >
                <FaChevronRight />
              </button>
            </div>

            <button
              onClick={handleRemoveSelected}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded"
            >
              <FaTrash />
              Remove
            </button>
          </div>

          <ToastContainer position="top-right" autoClose={3000} />
        </div>
      </div>
    </ProjectPreOrderLayoutWrapper>
  );
};

export default WorkLocationList;
